'use client';

import React from 'react';
import { Spinner, type SpinnerSize } from '@/core/presentation/components/ui/Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'loading'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Desabilita o botão e exibe indicador de carregamento. */
  loading?: boolean;
  /** Texto (ou nó) exibido junto ao spinner quando `loading` é true. Se omitido, repete `children`. */
  loadingLabel?: React.ReactNode;
  children: React.ReactNode;
}

const spinnerSizeForButton: Record<ButtonSize, SpinnerSize> = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-br from-cyan-dark to-cyan text-navy-dark font-bold hover:shadow-[0_12px_40px_rgba(126,207,239,0.4)] active:translate-y-0',
  secondary:
    'bg-navy-mid/80 text-white border border-white/15 hover:bg-navy-mid hover:border-cyan/30',
  outline:
    'bg-transparent text-cyan border-2 border-cyan hover:bg-cyan/10',
  ghost: 'bg-transparent text-white/80 hover:bg-white/10 hover:text-white',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'text-xs px-3 py-1.5 rounded-lg',
  md: 'text-xs font-bold px-4 py-2 rounded-full',
  lg: 'text-sm font-bold px-5 py-2.5 rounded-full',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      className = '',
      disabled,
      loading = false,
      loadingLabel,
      children,
      ...props
    },
    ref
  ) => {
    const base =
      'inline-flex items-center justify-center gap-2 transition-[color,background-color,box-shadow,opacity] duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark';
    const variantClass = variantClasses[variant];
    const sizeClass = sizeClasses[size];
    const widthClass = fullWidth ? 'w-full' : '';
    const isBusy = loading;
    const spinnerSize = spinnerSizeForButton[size];
    /**
     * Sempre por último: sobrescreve variant + `className` do consumidor (ex. `ghost` + `hover:bg-white/10`,
     * `focus-visible:ring`, `hover:underline`) enquanto `loading` — antes `className` vinha depois e reativava o “card”.
     */
    const busyTail =
      isBusy ?
        [
          'pointer-events-none overflow-hidden [-webkit-tap-highlight-color:transparent]',
          /** Chrome pode manter `:hover` após `disabled` até mover o mouse — neutraliza só hover/active. */
          '[aria-busy="true"]:hover:!bg-transparent [aria-busy="true"]:active:!bg-transparent',
          'hover:!bg-transparent hover:!shadow-none hover:!no-underline',
          'active:!bg-transparent',
          'focus:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!shadow-none',
          'disabled:!opacity-100 disabled:hover:!bg-transparent',
        ].join(' ')
      : '';

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || isBusy}
        aria-busy={isBusy || undefined}
        className={`${base} ${variantClass} ${sizeClass} ${widthClass} ${className} ${busyTail}`.trim()}
        {...props}
      >
        {isBusy ?
          <span className="inline-flex min-w-0 items-center justify-center gap-2" key="busy">
            <Spinner size={spinnerSize} />
            {loadingLabel != null ? loadingLabel : children}
          </span>
        : <span className="inline-flex min-w-0 items-center justify-center gap-2" key="idle">{children}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
